/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

/** The sort names are IQ's own; the backend sends them to IQ unchanged. */
export const EVALUATION_SORT_FIELDS = [
  'PUBLIC_ID',
  'FORMAT',
  'LAST_EVALUATION_TIME',
  'COMPONENTS_EVALUATED',
  'CRITICAL_VIOLATION_COUNT',
  'SEVERE_VIOLATION_COUNT',
  'MODERATE_VIOLATION_COUNT',
] as const;
export type EvaluationSortField = (typeof EVALUATION_SORT_FIELDS)[number];

export const THREAT_LEVELS = ['critical', 'severe', 'moderate'] as const;
export type ThreatLevel = (typeof THREAT_LEVELS)[number];

/** Only one of "with" and "without" can apply; null means both. */
export type ViolationsFilter = 'with' | 'without' | null;

/** The ids of the filter sections: the View names its sections with them and the hook maps them back to state. */
export const FILTER_IDS = {
  format: 'format',
  threatLevel: 'threatLevel',
  violations: 'violations',
} as const;

export const PAGE_SIZES = [10, 25, 50] as const;
export const DEFAULT_PAGE_SIZE = 25;

/** Matches EvaluationsQuery.MAX_SEARCH_LENGTH in the backend: longer search text is rejected with a 400. */
export const MAX_SEARCH_LENGTH = 255;

/** Everything the list depends on. It lives in the URL, so a reload or a shared link shows the same list. */
export interface EvaluationsQuery {
  formats: string[];
  threatLevels: ThreatLevel[];
  violations: ViolationsFilter;
  search: string;
  sortBy: EvaluationSortField;
  asc: boolean;
  /** 1-based, like the backend and TablePagination. */
  page: number;
  pageSize: number;
}

export const DEFAULT_QUERY: EvaluationsQuery = {
  formats: [],
  threatLevels: [],
  violations: null,
  search: '',
  sortBy: 'PUBLIC_ID',
  asc: true,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export interface EvaluationRow {
  /** IQ's id of the repository, for the future detail link. */
  repositoryId: string;
  name: string;
  format: string;
  quarantineEnabled: boolean;
  /** ISO-8601 instant in UTC, or null when IQ has no evaluation of the repository. */
  lastEvaluationTime: string | null;
  componentsEvaluated: number;
  criticalViolationCount: number;
  severeViolationCount: number;
  moderateViolationCount: number;
  quarantinedComponentCount: number;
  reevaluationRunning: boolean;
}

export interface EvaluationsPage {
  /** Repositories the user can see, before any filter. */
  total: number;
  /** Repositories matching every filter, across all pages. */
  filtered: number;
  page: number;
  pageSize: number;
  /** The repository formats the format filter accepts. */
  formats: string[];
  results: EvaluationRow[];
}
